import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '@/services/apiClient';
import { 
  FarmInvitation, 
  FarmInvitationCreate, 
  FarmInvitationListResponse,
  FarmUserRole,
  FarmUserRoleUpdate 
} from '@/types/farms';

interface FarmAccessState {
  loading: boolean;
  error: string | null;
  farmUsers: Record<string, FarmUserRole[]>; // farm_id -> users with roles
  farmInvitations: Record<string, FarmInvitation[]>; // farm_id -> pending invitations
}

const initialState: FarmAccessState = {
  loading: false,
  error: null,
  farmUsers: {},
  farmInvitations: {}
};

// Async thunk to fetch users for a farm
export const fetchFarmUsersAsync = createAsyncThunk(
  'farmAccess/fetchFarmUsers',
  async (farmId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{ users: FarmUserRole[] }>(`/farms/${farmId}/users`);
      return { farmId, users: response.users || [] };
    } catch (error: any) {
      console.error('fetchFarmUsersAsync - Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch farm users');
    }
  }
);

// Async thunk to fetch pending invitations for a farm
export const fetchFarmInvitationsAsync = createAsyncThunk(
  'farmAccess/fetchFarmInvitations',
  async ({ farmId, status }: { farmId: string; status?: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled' }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) {
        params.append('status', status);
      }
      const url = `/farms/${farmId}/invitations${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<FarmInvitationListResponse>(url);
      return { farmId, invitations: response.invitations || [] };
    } catch (error: any) {
      console.error('fetchFarmInvitationsAsync - Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch farm invitations');
    }
  }
);

// Async thunk to update a user's role in a farm
export const updateUserRoleAsync = createAsyncThunk(
  'farmAccess/updateUserRole',
  async ({ farmId, userId, roleUpdate }: { farmId: string; userId: number; roleUpdate: FarmUserRoleUpdate }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put<{ message: string; user_role: FarmUserRole }>(
        `/farms/${farmId}/users/${userId}`,
        roleUpdate
      );
      return { farmId, userId, userRole: response.user_role };
    } catch (error: any) {
      console.error('updateUserRoleAsync - Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to update user role');
    }
  }
);

// Async thunk to remove a user from a farm
export const removeUserFromFarmAsync = createAsyncThunk(
  'farmAccess/removeUserFromFarm',
  async ({ farmId, userId }: { farmId: string; userId: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete<{ message: string }>(`/farms/${farmId}/users/${userId}`);
      return { farmId, userId, message: response.message };
    } catch (error: any) {
      console.error('removeUserFromFarmAsync - Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to remove user from farm');
    }
  }
);

// Async thunk to invite a user to a farm
export const inviteUserToFarmAsync = createAsyncThunk(
  'farmAccess/inviteUserToFarm',
  async ({ farmId, invitation }: { farmId: string; invitation: FarmInvitationCreate }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<{ message: string; invitation?: FarmInvitation }>(
        `/farms/${farmId}/invitations`,
        invitation
      );
      return { farmId, invitation: response.invitation, message: response.message };
    } catch (error: any) {
      console.error('inviteUserToFarmAsync - Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to invite user to farm');
    }
  }
);

// Async thunk to cancel an invitation
export const cancelInvitationAsync = createAsyncThunk(
  'farmAccess/cancelInvitation',
  async ({ farmId, invitationId }: { farmId: string; invitationId: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete<{ message: string }>(`/farms/${farmId}/invitations/${invitationId}`);
      return { farmId, invitationId, message: response.message };
    } catch (error: any) {
      console.error('cancelInvitationAsync - Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to cancel invitation');
    }
  }
);

// Async thunk to resend an invitation
export const resendInvitationAsync = createAsyncThunk(
  'farmAccess/resendInvitation',
  async ({ farmId, invitationId }: { farmId: string; invitationId: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<{ message: string; invitation: FarmInvitation }>(
        `/farms/${farmId}/invitations/${invitationId}/resend`,
        {}
      );
      return { farmId, invitation: response.invitation, message: response.message };
    } catch (error: any) {
      console.error('resendInvitationAsync - Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to resend invitation');
    }
  }
);

const farmAccessSlice = createSlice({
  name: 'farmAccess',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearFarmUsers: (state, action) => {
      const farmId = action.payload;
      delete state.farmUsers[farmId];
    },
    clearFarmInvitations: (state, action) => {
      const farmId = action.payload;
      delete state.farmInvitations[farmId];
    },
    clearAllFarmAccess: (state) => {
      state.farmUsers = {};
      state.farmInvitations = {};
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch farm users
      .addCase(fetchFarmUsersAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFarmUsersAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { farmId, users } = action.payload;
        state.farmUsers[farmId] = users;
      })
      .addCase(fetchFarmUsersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch farm invitations
      .addCase(fetchFarmInvitationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFarmInvitationsAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { farmId, invitations } = action.payload;
        state.farmInvitations[farmId] = invitations;
      })
      .addCase(fetchFarmInvitationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update user role
      .addCase(updateUserRoleAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserRoleAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { farmId, userId, userRole } = action.payload;
        
        // Update the user role in the cached farm users
        if (state.farmUsers[farmId]) {
          const userIndex = state.farmUsers[farmId].findIndex(u => u.user_id === userId);
          if (userIndex !== -1) {
            state.farmUsers[farmId][userIndex] = userRole;
          }
        }
      })
      .addCase(updateUserRoleAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Remove user from farm
      .addCase(removeUserFromFarmAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeUserFromFarmAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { farmId, userId } = action.payload;
        
        // Remove user from cached farm users
        if (state.farmUsers[farmId]) {
          state.farmUsers[farmId] = state.farmUsers[farmId].filter(u => u.user_id !== userId);
        }
      })
      .addCase(removeUserFromFarmAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Invite user to farm
      .addCase(inviteUserToFarmAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(inviteUserToFarmAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { farmId, invitation } = action.payload;
        
        // If invitation was created (not direct assignment), add to cached invitations
        if (invitation && state.farmInvitations[farmId]) {
          state.farmInvitations[farmId] = [invitation, ...state.farmInvitations[farmId]];
        }
        
        // Clear farm users cache to trigger refetch (in case it was direct assignment)
        if (state.farmUsers[farmId]) {
          delete state.farmUsers[farmId];
        }
      })
      .addCase(inviteUserToFarmAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Cancel invitation
      .addCase(cancelInvitationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelInvitationAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { farmId, invitationId } = action.payload;
        
        // Remove invitation from cached invitations
        if (state.farmInvitations[farmId]) {
          state.farmInvitations[farmId] = state.farmInvitations[farmId].filter(
            inv => inv.id !== invitationId
          );
        }
      })
      .addCase(cancelInvitationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Resend invitation
      .addCase(resendInvitationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendInvitationAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { farmId, invitation } = action.payload;
        
        // Update the invitation in cached invitations
        if (state.farmInvitations[farmId]) {
          const invIndex = state.farmInvitations[farmId].findIndex(inv => inv.id === invitation.id);
          if (invIndex !== -1) {
            state.farmInvitations[farmId][invIndex] = invitation;
          }
        }
      })
      .addCase(resendInvitationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearFarmUsers, clearFarmInvitations, clearAllFarmAccess } = farmAccessSlice.actions;

// Selectors
export const selectFarmAccess = (state: { farmAccess: FarmAccessState }) => state.farmAccess;

// Stable reference for the "no entry yet" case. `x[farmId] || []` would mint a
// fresh empty array on every call, producing a new reference each render
// (unnecessary re-renders / the selector stability warning). Reusing one frozen
// array keeps the reference stable until the slice actually populates the entry.
const EMPTY_LIST: readonly never[] = Object.freeze([]);
export const selectFarmUsers = (farmId: string) => (state: { farmAccess: FarmAccessState }) =>
  state.farmAccess.farmUsers[farmId] || EMPTY_LIST;
export const selectFarmInvitations = (farmId: string) => (state: { farmAccess: FarmAccessState }) =>
  state.farmAccess.farmInvitations[farmId] || EMPTY_LIST;
export const selectFarmAccessLoading = (state: { farmAccess: FarmAccessState }) => 
  state.farmAccess.loading;
export const selectFarmAccessError = (state: { farmAccess: FarmAccessState }) => 
  state.farmAccess.error;

export default farmAccessSlice.reducer;
